import { useState, useEffect } from 'react';
import { useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { AuthShell } from '@/components/AuthShell';
import { LangSwitcher } from '@/components/LangSwitcher';

export function Login() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { state, login } = useAuth();
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [ssoError, setSsoError] = useState('');

  useEffect(() => {
    fetch('/internal/v1/auth/sso/status')
      .then((r) => r.json())
      .then((data: { enabled: boolean }) => setSsoEnabled(data.enabled))
      .catch(() => setSsoEnabled(false));

    const err = searchParams.get('sso_error');
    if (err) {
      setSsoError(t(`login.ssoErrors.${err}`, { defaultValue: t('login.ssoErrors.unknown') }));
      searchParams.delete('sso_error');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state.status === 'authenticated') return <Navigate to="/" replace />;
  if (state.status === 'totp_setup') return <Navigate to="/totp-setup" replace />;
  if (state.status === 'totp_verify') return <Navigate to="/2fa" replace />;
  if (state.status === 'password_expired') return <Navigate to="/force-change-password" replace />;

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError(t('login.errorBothFields'));
      return;
    }

    setIsLoading(true);
    try {
      await login(email, password);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('login.errorGeneric'));
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <AuthShell>
      <div>
        <h2 className="text-xl font-bold text-white">{t('login.title')}</h2>
        <p className="text-white/60 mt-1 text-sm">{t('login.subtitle')}</p>
      </div>

      <LangSwitcher variant="glass" />

      <form onSubmit={(e) => void handleLogin(e)} className="space-y-4">
        {ssoError && (
          <div className="px-4 py-2.5 rounded-lg bg-amber-500/20 border border-amber-400/30 text-amber-200 text-sm font-medium">
            {ssoError}
          </div>
        )}

        {error && (
          <div className="px-4 py-2.5 rounded-lg bg-red-500/20 border border-red-400/30 text-red-200 text-sm font-medium">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-1.5">
            {t('login.emailLabel')}
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('login.emailPlaceholder')}
            disabled={isLoading}
            autoComplete="email"
            className="glass-input w-full px-4 py-2.5 rounded-lg text-sm text-white placeholder:text-white/50 focus:outline-none focus:border-brand-rose focus:bg-white/[0.22] focus:ring-[3px] focus:ring-brand-rose/25 disabled:opacity-50"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor="password" className="block text-sm font-medium text-white/80">
              {t('login.passwordLabel')}
            </label>
            <button
              type="button"
              onClick={() => void navigate('/forgot-password')}
              className="text-xs font-medium text-white/60 hover:text-white transition-colors underline underline-offset-2"
            >
              {t('login.forgotPassword')}
            </button>
          </div>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('login.passwordPlaceholder')}
              disabled={isLoading}
              autoComplete="current-password"
              className="glass-input w-full px-4 py-2.5 rounded-lg text-sm pr-12 text-white placeholder:text-white/50 focus:outline-none focus:border-brand-rose focus:bg-white/[0.22] focus:ring-[3px] focus:ring-brand-rose/25 disabled:opacity-50"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-2.5 rounded-lg text-white font-semibold text-sm shadow-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose hover:-translate-y-px hover:shadow-[0_8px_25px_rgba(237,18,95,0.4)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center gap-2"
        >
          {isLoading && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
          {isLoading ? t('login.signingIn') : t('login.signIn')}
        </button>
      </form>

      <div className="flex items-center gap-4 text-xs text-white/40 font-medium">
        <div className="flex-1 h-px bg-white/[0.22]" />
        <span>{t('login.or')}</span>
        <div className="flex-1 h-px bg-white/[0.22]" />
      </div>

      <button
        type="button"
        disabled={!ssoEnabled}
        onClick={() => {
          if (ssoEnabled) window.location.href = '/internal/v1/auth/sso/login';
        }}
        title={ssoEnabled ? t('login.microsoftSso') : t('login.ssoUnavailable')}
        className={
          ssoEnabled
            ? 'w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-white/10 border border-white/[0.15] hover:bg-white/15 transition-colors cursor-pointer'
            : 'w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-white/40 bg-white/5 border border-white/[0.15] cursor-not-allowed'
        }
      >
        <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
          <rect x="1" y="1" width="9" height="9" fill="#f25022" opacity={ssoEnabled ? '1' : '0.4'} />
          <rect x="11" y="1" width="9" height="9" fill="#7fba00" opacity={ssoEnabled ? '1' : '0.4'} />
          <rect x="1" y="11" width="9" height="9" fill="#00a4ef" opacity={ssoEnabled ? '1' : '0.4'} />
          <rect x="11" y="11" width="9" height="9" fill="#ffb900" opacity={ssoEnabled ? '1' : '0.4'} />
        </svg>
        {t('login.microsoftSso')}
      </button>

      <p className="text-center text-xs text-white/35 pt-2">{t('login.noAccount')}</p>
    </AuthShell>
  );
}
